import { PaymentIcon, type PaymentType, getCardType } from 'react-svg-credit-card-payment-icons'

/**
 * Maps backend cardBrand strings (lowercase) to PaymentIcon type names.
 * NMI gateway returns short codes like 'mc', 'disc', 'amex', 'visa'.
 */
const BRAND_TO_TYPE: Record<string, PaymentType> = {
	visa: 'Visa',
	mc: 'Mastercard',
	mastercard: 'Mastercard',
	amex: 'AmericanExpress',
	americanexpress: 'AmericanExpress',
	american_express: 'AmericanExpress',
	disc: 'Discover',
	discover: 'Discover',
	diners: 'DinersClub',
	dinersclub: 'DinersClub',
	diners_club: 'DinersClub',
	jcb: 'JCB',
	maestro: 'Maestro',
	unionpay: 'UnionPay',
	elo: 'Elo',
	hipercard: 'Hipercard',
	mir: 'Mir',
}

/**
 * Resolve a backend card brand string to a PaymentType.
 * Falls back to 'Generic' for unrecognised brands.
 */
export function resolveCardBrand(brand: string | null | undefined): PaymentType {
	if (!brand) return 'Generic' as PaymentType
	return BRAND_TO_TYPE[brand.toLowerCase()] ?? ('Generic' as PaymentType)
}

interface CardBrandIconProps {
	/** Backend card brand string (e.g. 'visa', 'mastercard', 'amex') */
	brand?: string | null
	/** Width in pixels (height auto-scales to maintain aspect ratio) */
	width?: number
	className?: string
}

/**
 * Renders an SVG card brand icon from a backend cardBrand string.
 * Uses the `flatRounded` format for a polished look on dark backgrounds.
 */
export default function CardBrandIcon({ brand, width = 40, className }: CardBrandIconProps) {
	const type = resolveCardBrand(brand)
	return (
		<PaymentIcon
			type={type}
			format="flatRounded"
			width={width}
			className={className}
		/>
	)
}

export { getCardType }
